import { DocumentReference, doc, getFirestore } from 'firebase/firestore';

/**
 * Context identifiers for determining the active form/entity context and associated IDs
 */
interface ContextIds {
  /** Whether the component is inside a form context */
  insideForm: boolean;
  /** Whether the component is inside an entity context */
  insideEntity: boolean;
  /** Form document ID (required when insideForm is true) */
  formId?: string;
  /** Form version document ID (required when insideForm is true) */
  versionId?: string;
  /** Entity document ID (required when insideEntity is true) */
  entityId?: string;
  /** Entity record document ID (required when insideEntity is true) */
  recordId?: string;
}

/**
 * Resolved context information including Firestore document reference and path string
 */
interface ContextResolution {
  /** Firestore document reference for the resolved context, null if context cannot be resolved */
  docRef: DocumentReference | null;
  /** String path to the document (e.g., "entities/Projects/records/123" or "forms/abc/formVersions/xyz") */
  path: string;
}

/**
 * Resolves the correct Firestore document reference and path based on active contexts.
 * 
 * **Context Precedence:**
 * Entity context is checked first because entities can be nested inside forms.
 * When both contexts are active (entity within a form), saves must target the
 * entity record document, not the form version document.
 * 
 * **Resolution Order:**
 * 1. If inside entity context with valid entityId and recordId:
 *    Returns: `entities/{entityId}/records/{recordId}`
 * 
 * 2. Else if inside form context with valid formId and versionId:
 *    Returns: `forms/{formId}/formVersions/{versionId}`
 * 
 * 3. Otherwise:
 *    Returns: null docRef and "unknown" path
 * 
 * **Usage Example:**
 * ```typescript
 * const { docRef, path } = resolveContextRoutes({
 *   insideForm,
 *   insideEntity,
 *   formId,
 *   versionId,
 *   entityId,
 *   recordId
 * });
 * 
 * if (!docRef) {
 *   throw new Error('Missing required context for saving');
 * }
 * 
 * await setDoc(docRef, updateData, { merge: true });
 * const promiseId = `${path}#field:${fieldId}:${Date.now()}`;
 * ```
 * 
 * @param context - Object containing context flags and document IDs
 * @returns Object with docRef (DocumentReference or null) and path (string)
 */
export const resolveContextRoutes = (
  context: ContextIds
): ContextResolution => {
  const firestore = getFirestore();
  
  if (context.insideEntity && context.entityId && context.recordId) {
    return {
      docRef: doc(firestore, 'entities', context.entityId, 'records', context.recordId),
      path: `entities/${context.entityId}/records/${context.recordId}`
    };
  }
  
  if (context.insideForm && context.formId && context.versionId) {
    return {
      docRef: doc(firestore, 'forms', context.formId, 'formVersions', context.versionId),
      path: `forms/${context.formId}/formVersions/${context.versionId}`
    };
  }
  
  return {
    docRef: null,
    path: 'unknown'
  };
};

